import { MarketplacePartnerTierType } from '@atlassian/mpac-types/graphql-gateway-types';

import type { BundleConfig } from './bundleConfig';

import TestManagementBundleHero from '../assets/qa_bundle_tile.png';
import FooterLeftImage from '../assets/test-management/tm_footer_left_image.png';
import FooterRightImage from '../assets/test-management/tm_footer_right_image.png';
import Opportunity1Icon from '../assets/test-management/tm_opportunity1_icon.svg';
import Opportunity2Icon from '../assets/test-management/tm_opportunity2_icon.svg';
import Opportunity3Icon from '../assets/test-management/tm_opportunity3_icon.svg';
import Opportunity4Icon from '../assets/test-management/tm_opportunity4_icon.svg';
import TestManagementBundleHeader from '../assets/test-management/tm-header.svg';

const TEST_MANAGEMENT_APPS = [
  {
    title: 'AIO Tests: QA Testing and Test Management',
    description: 'Test case traceability',
    icon: 'https://marketplace.atlassian.com/product-listing/files/aad02720-274f-4bd5-9849-59206b11c18a?fileType=image&mode=full-fit',
    iconAlt: 'AIO Testing Icon',
    url: 'https://marketplace.atlassian.com/apps/1222843/aio-tests-qa-testing-and-test-management-in-jira',
  },
  {
    title: 'ScriptRunner for Jira',
    description: 'Automated test workflows',
    icon: 'https://marketplace.atlassian.com/product-listing/files/73ebab0a-b3e9-4697-9322-9539f5c27965?fileType=image&mode=full-fit',
    iconAlt: 'ScriptRunner Icon',
    url: 'https://marketplace.atlassian.com/apps/6820/scriptrunner-for-jira',
  },
];

const getTestManagementBenefits = (terminology: string) => [
  {
    title: 'Limited test visibility and traceability',
    description:
      'Scattered test data and disconnected tracking make it difficult to monitor coverage, execution status, and quality trends across cycles.',
    icon: Opportunity1Icon,
    iconAlt: 'Traceability Icon',
  },
  {
    title: 'Manual and repetitive QA tasks',
    description:
      'Teams spend significant time on repetitive tasks like setting up environments or validating post-test conditions, slowing down releases.',
    icon: Opportunity2Icon,
    iconAlt: 'Test Execution Icon',
  },
  {
    title: `Disconnected testing and ${terminology} management`,
    description: `Fragmented tools and workflows between QA, development, and product teams lead to miscommunication, inefficiencies, and missed ${terminology}s.`,
    icon: Opportunity3Icon,
    iconAlt: 'Reporting Icon',
  },
  {
    title: 'Compliance and audit challenges',
    description:
      'Organizations in regulated industries require strict traceability, which is hard to maintain manually.',
    icon: Opportunity4Icon,
    iconAlt: 'Compliance Icon',
  },
];

const getTestManagementAppsSections = () => [
  {
    title: 'Improve traceability and compliance',
    description:
      'Connect requirements, test cases, and defects within Jira - ensuring full traceability and structured documentation for audits.',
    appDetails: {
      title: 'AIO Tests: QA Testing and Test Management in Jira',
      vendor: 'by Navarambh Software Pvt. Ltd.',
      partnerTier: MarketplacePartnerTierType.Platinum,
      rating: '4/4',
      reviewCount: '98',
      installOrDownloadsText: '4.3k installs',
      badge: 'cloud-fortified' as const,
    },
    videoId: 'KZF0QCEDjsA',
  },
  {
    title: 'Automate repetitive QA workflows',
    description:
      'Enable custom automation for test environment setup, validation scripts, and post-test actions - reducing manual effort and accelerating feedback loops.',
    appDetails: {
      title: 'ScriptRunner for Jira',
      vendor: 'by Adaptavist',
      partnerTier: MarketplacePartnerTierType.Platinum,
      rating: '3.7/4',
      reviewCount: '804',
      installOrDownloadsText: '38k installs',
      badge: 'cloud-fortified' as const,
    },
    videoId: 'NWS2MxIvmrE',
  },
];

export function testManagementBundleConfig(enableIssueTerminology: boolean): BundleConfig {
  const terminology = enableIssueTerminology ? 'work item' : 'issue';

  return {
    hero: {
      breadcrumbTitle: 'Test Management Bundle',
      title: 'QA Track Power Suite',
      description:
        'Ensure software quality with a fully integrated test management suite designed to streamline every stage of your QA process, from planning to reporting.',
      requestButton: 'Request now',
      headerImage: TestManagementBundleHeader,
      headerImageAlt: 'Test Management Bundle Header Image',
      heroImage: TestManagementBundleHero,
      heroImageAlt: 'Test Management Bundle Hero Image',
    },
    apps: TEST_MANAGEMENT_APPS,
    benefits: {
      title: 'Remove the obstacles to effective test management',
      description:
        'Inefficiencies and gaps make it challenging to maintain quality, meet compliance standards, and streamline execution.',
      items: getTestManagementBenefits(terminology),
    },
    appsSection: {
      title: 'An easier way to work better together',
      description:
        'Streamline your QA process with powerful tools for test management, automation, and end-to-end traceability.',
      sections: getTestManagementAppsSections(),
    },
    footer: {
      title: 'Looking to elevate your testing process?',
      description:
        'Boost traceability, streamline execution, and stay audit-ready with better test management tools.',
      requestButton: 'Request now',
      leftImageAlt: 'Team testing left',
      rightImageAlt: 'Team testing right',
      leftImage: FooterLeftImage,
      rightImage: FooterRightImage,
    },
  };
}
